import React from "react";
import {
  PlusIcon,
  MinusIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { ClockIcon } from "@heroicons/react/24/solid";
import { formatDateTime } from "@/utils/formatDate";


const EtCreationCard = ({
  creationItem,
  index,
  showDetail,
  toggleShowDetail,
  handleEdit,
  createMarkup,
  showOwner = false, // 25 Jan 2026 - NEW (for Collective → Everyone)
  mode="full",       // 25 Jan 2026 
}) => {

  // First Created - 09 Dec 2025
  // Author - Atul Pandey
  // Purpose - Lightweight card to display a single Creation in Trinity Creation Center (Log View)
  // Updated - 25 Jan 2026 - Refinements for showOwner - Collective
  // Updated - 30 Jan 2026 - console.log to troubleshoot incoming data structure change for PN Universe


  // console.log('creationItem', creationItem) // Debug - 30 Jan 2026

  // -----------------------------------------
  // 1. Safe Field Extraction
  // -----------------------------------------
  const {
    _id,
    userCreationName,
    userCreationDesc,
    createdAt,
    userCreationNotes = [],
  } = creationItem;

  const notesCount = userCreationNotes.length || 0;

  // --- NEW: Local toggle state for creation notes ---
  const [showNotes, setShowNotes] = React.useState(false);
  const [showNoteDetails, setShowNoteDetails] = React.useState({});


  // -----------------------------------------
  // 2. Handlers
  // -----------------------------------------
  const handleToggle = () => toggleShowDetail(_id);
  const handleEditClick = () => handleEdit(creationItem);

  const isExpanded = !!showDetail[_id];

  if (!creationItem) return null;

  return (
    <div className="border rounded bg-gray-50 hover:bg-gray-100 transition-colors duration-150">
      {/* --- 2A. Header Row --- */}
      <div className="grid grid-cols-12 items-center px-3 py-2">
        {/* Index + Toggle */}
        <div className="col-span-4 flex items-center">
          <button
            type="button"
            onClick={handleToggle}
            className="mx-1 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center"
          >
            {isExpanded ? (
              <MinusIcon className="h-4 w-4 text-orange-600" />
            ) : (
              <PlusIcon className="h-4 w-4 text-orange-600" />
            )}
          </button>

          <p className="text-gray-600 font-bold text-lg mx-1 px-1">
            {index + 1}.
          </p>

          {/* 25 Jan 2026 - Owner (Collective → Everyone) */}
          {showOwner && creationItem?.userId?._id && (
            <div className="ml-3 mr-2 flex items-center gap-2 bg-white rounded-full px-2 py-[2px] border">
                <div className="h-5 w-5 rounded-full bg-orange-100 flex items-center justify-center text-[10px] font-bold text-orange-700">
                {(creationItem.userId.name || creationItem.userId.username || "U")[0]}
                </div>
                <p className="text-[10px] font-semibold text-gray-700">
                {(creationItem.userId.name || creationItem.userId.username || "User").split(" ")[0]}
                </p>
            </div>
           )}

          <p className="text-gray-800 text-xs md:text-sm mx-1 px-1 line-clamp-2">
            {userCreationName || "Untitled Creation"}
          </p>
        </div>

        {/* Created At */}
        <div className="col-span-5 flex items-center justify-start md:justify-center mt-2 md:mt-0">
          <ClockIcon className="h-4 w-4 text-lime-600" />
          <p className="text-[10px] mx-1 text-gray-600">First Created:</p>
          <p className="text-[10px] text-gray-500 mx-1">
            {createdAt ? formatDateTime(createdAt) : "N/A"}
          </p>
        </div>

        {/* Notes + Edit */}
        <div className="col-span-3 flex items-center justify-end space-x-3 mt-2 md:mt-0">
          <div className="flex items-center">
            <p className="text-[10px] text-gray-500 mr-1">Notes:</p>
            <span className="h-5 w-5 rounded-full bg-white border border-orange-300 flex items-center justify-center text-[10px] text-orange-600">
              {notesCount}
            </span>
          </div>

        {/* 25 Jan 2026 - conditioned edit based on mode */}
        {mode === "full" && (
          <button
            type="button"
            onClick={handleEditClick}
            className="rounded-full h-6 w-6 hover:bg-white flex justify-center items-center"
          >
            <PencilSquareIcon className="h-4 w-4 text-orange-600" />
          </button>
         )}
        </div>
      </div>

      {/* --- 2B. Collapsible Body --- */}
      {isExpanded && (
        <div className="border-t bg-white px-3 py-2">
          {/* Description */}
          {userCreationDesc && (
            <div className="mb-2">
              <p className="inline-block border-b-2 border-orange-600 text-gray-800 font-semibold text-xs md:text-sm mb-1">
                Description
              </p>
              <div
                className="text-[11px] md:text-xs leading-4 text-gray-700 mt-1 fix-nested-lists"
                dangerouslySetInnerHTML={createMarkup(userCreationDesc)}
              />
            </div>
          )}

          {/* --- Creation Notes Trail (full list) --- */}
            {notesCount > 0 && (
        <div className="mt-3 p-2 bg-gray-100 rounded">

            {/* Header Row */}
            <div className="flex items-center cursor-pointer" onClick={() => setShowNotes(!showNotes)}>
            <div className="rounded-full h-5 w-5 flex justify-center items-center hover:bg-white">
                {showNotes ? (
                <MinusIcon className="h-4 w-4 text-orange-600" />
                ) : (
                <PlusIcon className="h-4 w-4 text-orange-600" />
                )}
            </div>

            <p className="mx-2 font-bold text-gray-700 text-xs md:text-sm">
                Creation Notes Trail
            </p>

            <p className="text-gray-400 text-[10px] italic">
                what perspectives did I gain?
            </p>
            </div>

            {/* Notes List */}
            {showNotes &&
                userCreationNotes.map((note, nIdx) => {
                    const noteText = note?.userCreationNoteText || "";
                    const noteDate = note?.userCreationNoteDate || "";

                    return (
                    <div key={note._id + nIdx} className="mt-2">

                        {/* Note Summary Row */}
                        <div className="grid grid-cols-6 items-center bg-gray-50 border rounded p-2">
                        
                        {/* Expand toggle + Index */}
                        <div className="col-span-2 flex items-center">
                            <div
                            onClick={() =>
                                setShowNoteDetails((prev) => ({
                                ...prev,
                                [note._id]: !prev[note._id],
                                }))
                            }
                            className="mx-1 rounded-full h-5 w-5 flex justify-center items-center cursor-pointer hover:bg-white"
                            >
                            {showNoteDetails[note._id] ? (
                                <MinusIcon className="h-4 w-4 text-orange-600" />
                            ) : (
                                <PlusIcon className="h-4 w-4 text-orange-600" />
                            )}
                            </div>

                            <p className="text-gray-600 font-semibold text-sm mx-1">
                            {nIdx + 1}.
                            </p>
                        </div>

                        {/* Note Text Preview */}
                        <div
                            className="col-span-3 text-[11px] text-gray-700 line-clamp-1 truncate px-1"
                            dangerouslySetInnerHTML={createMarkup(noteText)}
                        />

                        {/* Date */}
                        <div className="col-span-1 flex items-center justify-end">
                            <ClockIcon className="h-4 w-4 text-lime-600" />
                            <p className="text-[9px] text-gray-500 mx-1">
                            {formatDateTime(noteDate)}
                            </p>
                        </div>
                        </div>

                        {/* Note Detail Expanded */}
                        {showNoteDetails[note._id] && (
                        <div className="bg-white border rounded mt-1 p-2">
                            <div
                            dangerouslySetInnerHTML={createMarkup(noteText)}
                            className="text-[11px] leading-4 text-gray-700 fix-nested-lists"
                            />
                        </div>
                        )}
                    </div>
                    );
                })}
            </div>
            )}

        </div>
      )}
    </div>
  );
};

export default EtCreationCard;